/**
 * Branch
 *
 * Filename: page_context.ts
 *
 * @category  Fahrradxxl
 * @author    Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 * @copyright 2026 Fahrrad-xxl.de
 */



/* 📜 INFORMATION
 * --------------------------------------------------------------------------------------------------------------------
 *  This Class was born out of the need to check for specific URL Params
 *  at the earliest time possible in JS and possibly set system-wide variables
 *  to be used at given times. ( Basically "reacting" to things in the URL. )
 *
 * Example:
 *  Sometimes the URL contains utm_ Params like `utm_source`, `utm_campaign`, etc.
 *  Those get lost with switching to the next page, but the information is still valuable
 *  for the whole user session, because it tells a user came from a specific place,
 *  for instance a newsletter email.
 *
 * This Class should mostly serve as system-wide helper, so no initialisation is intended.
 */



/* 📑 TODO
 * --------------------------------------------------------------------------------------------------------------------
 * -
 */






/* 📦️ IMPORTS
 * ----------------------------------------------------------------------------------------------------------------- */
import FxxlCore  from '@fxxl/core';
import FxxlError from '@fxxl/error';
import FxxlURL   from '@fxxl/fxxl_url';






/* 📑 ENUMS & TYPES & CONSTANTS
 * ----------------------------------------------------------------------------------------------------------------- */
// Error Messages
enum ERROR_MESSAGE {
    INSTANCE_EXISTS = 'Error: FxxlPageContext: Instance already exists',
}

enum URL_PARAMS {
    UTM_ID      = 'utm_id',
    UTM_SOURCE  = 'utm_source',
    UTM_MEDIUM  = 'utm_medium',
    UTM_CAMPAIN = 'utm_campaign',
    UTM_TERM    = 'utm_term',
    UTM_CONTENT = 'utm_content',
    MFILIALE    = 'mfilia',
}

const CONFIG_STORAGE = {
    UTM_NEWSLETTER: {
        KEY:             FxxlCore.getConfig()?.storageKeys?.utmSourceNewsletter,
        CONTENT:         FxxlCore.getStorage(FxxlCore.getConfig()?.storageKeys?.utmSourceNewsletter),
        EXPIRATION_TIME: Date.now() + (20 * 365 * 24 * 60 * 60 * 1000) // 20 Jahre
    }
};




/* 💾 INTERFACES
 * ----------------------------------------------------------------------------------------------------------------- */






/* ✒️ CLASS: FxxlPageContext
 * ----------------------------------------------------------------------------------------------------------------- */
export default class FxxlPageContext {

    /* 🏷️ VARS
     * ----------------------------------------------------------------------------------------- */
    private static _instance: FxxlPageContext;




    /* 🐙 STATIC HELPER: URL Contains
     * ----------------------------------------------------------------------------------------- */
    private static urlContains(paramKey: string, paramValue: string = '', url?: FxxlURL) {
        const currentUrl: FxxlURL = url || FxxlURL.getCurrent();

        const urlSearchParams: URLSearchParams = currentUrl?.nativeUrl?.searchParams;
        const foundParam: string | null = urlSearchParams?.get(paramKey);

        if (paramValue) {
            return foundParam === paramValue;
        }

        return !!foundParam;
    }

    public static urlContainsUTMSourceNewsletter(url?: FxxlURL) {
        return FxxlPageContext.urlContains(URL_PARAMS.UTM_SOURCE, 'newsletter', url);
    }

    public static urlContainsMfiliale(url?: FxxlURL) {
        return FxxlPageContext.urlContains(URL_PARAMS.MFILIALE, '', url);
    }




    /* 🐉 CONSTRUCTOR
     * ----------------------------------------------------------------------------------------- */
    constructor() {
        // EXIT CRITERIA: Only one Instance acceptable
        if (FxxlPageContext._instance) {
            new FxxlError(ERROR_MESSAGE.INSTANCE_EXISTS);
            return;
        }

        // BIND: Instance
        FxxlPageContext._instance = this;

        // INIT: Handler
        this.handleURLUTMSourceNewsletter();
    }




    /* 🧙 HANDLER
     * ----------------------------------------------------------------------------------------- */
    /*
     * Examines the URL Part of the Page Context
     * and based on if the URL contains "utm_source=newsletter"
     * a LocalStorage Variable will be build with Content from the URL Paramas
     */
    handleURLUTMSourceNewsletter() {
        if (!FxxlPageContext.urlContainsUTMSourceNewsletter()) {
            return;
        }

        // Check URL
        const url = FxxlURL.getCurrent();
        const urlSearchParams = url?.nativeUrl?.searchParams;

        // Set Local Storage ( Overwriting currently is intended )
        FxxlCore.setStorage(CONFIG_STORAGE.UTM_NEWSLETTER.KEY, {
            utmId:       urlSearchParams?.get(URL_PARAMS.UTM_ID)      || '',
            utmSource:   urlSearchParams?.get(URL_PARAMS.UTM_SOURCE)  || '',
            utmMedium:   urlSearchParams?.get(URL_PARAMS.UTM_MEDIUM)  || '',
            utmCampaign: urlSearchParams?.get(URL_PARAMS.UTM_CAMPAIN) || '',
            utmTerm:     urlSearchParams?.get(URL_PARAMS.UTM_TERM)    || '',
            utmContent:  urlSearchParams?.get(URL_PARAMS.UTM_CONTENT) || '',
        }, CONFIG_STORAGE.UTM_NEWSLETTER.EXPIRATION_TIME);
    }

}